import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Alert } from "@bdo/cornerstone-ui";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "@/components";
import type { WorkflowStepInstance } from "@/types";
import type { RootState } from "@/app/store";
import {
  useProceedWithOutputsMutation,
  useProceedWithAdditionalInformationMutation,
} from "@/app/workflows/workflowStepInstance";
import { useWorkflowErrorHandler } from "../hooks";
import {
  initialiseStepOutputState,
  resetStepOutputFeedback,
  selectCanSubmitStep,
  selectCanSubmitSelectStep,
  selectAnyOutputHasFeedback,
} from "@/features/workflow/outputStateSlice";
import {
  clearAllFieldState,
  selectCanSubmitAdditionalInformationStep,
} from "@/features/shared/feedbackFormSlice";
import type { SubmitAction } from "@/features/workflow/utils";

const AdditionalInformationStep = lazy(() =>
  import("@/features/workflow/outputs").then((m) => ({
    default: m.AdditionalInformationStep,
  })),
);

const StandardOutputStep = lazy(() =>
  import("@/features/workflow/outputs").then((m) => ({
    default: m.StandardOutputStep,
  })),
);

const FileOutputsStep = lazy(() =>
  import("@/features/workflow/outputs").then((m) => ({
    default: m.FileOutputsStep,
  })),
);

const SelectOutputStep = lazy(() =>
  import("@/features/workflow/outputs").then((m) => ({
    default: m.SelectOutputStep,
  })),
);

interface WorkflowStepRendererProps {
  workflowInstanceId: string;
  currentStep: WorkflowStepInstance | null | undefined;
  isWorkflowCompleted: boolean;
  isFinalStep: boolean;
  isAwaitingAgent: boolean;
  needsAdditionalInfo: boolean;
  onSubmitted: () => void;
  onSubmitError?: () => void;
  onSubmitActionChange: (action: SubmitAction | null) => void;
}

function getOptionsMode(
  currentStep: WorkflowStepInstance | null | undefined,
): "single" | "multiple" | null {
  if (!currentStep) return null;

  const properties =
    currentStep.outputs[0]?.workflowStepOutputDefinition.properties;
  if (!properties) return null;

  const isSingleSelect =
    properties.find((prop) => prop.key === "MultiSelect")?.value === false;

  return isSingleSelect ? "single" : "multiple";
}

export function WorkflowStepRenderer({
  workflowInstanceId,
  currentStep,
  isWorkflowCompleted,
  isFinalStep,
  isAwaitingAgent,
  needsAdditionalInfo,
  onSubmitted,
  onSubmitError,
  onSubmitActionChange,
}: Readonly<WorkflowStepRendererProps>) {
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.workflow",
  });

  const dispatch = useDispatch();
  const { extractErrorMessage } = useWorkflowErrorHandler();

  const [apiErrorMessage, setApiErrorMessage] = useState<{
    title: string;
    message: string;
  }>();

  // Initialise output state in Redux when step changes
  useEffect(() => {
    if (currentStep?.outputs && currentStep.outputs.length > 0) {
      dispatch(
        initialiseStepOutputState({
          stepInstanceId: currentStep.id,
          outputs: currentStep.outputs,
        }),
      );
    }
  }, [currentStep?.id, currentStep?.outputs, dispatch]);

  const [submitOutputs, { isLoading: isSubmitting }] =
    useProceedWithOutputsMutation();

  async function handleSubmit() {
    if (!currentStep) return;

    try {
      setApiErrorMessage(undefined);
      await submitOutputs({
        workflowInstanceId,
        workflowStepInstanceId: currentStep.id,
      }).unwrap();

      dispatch(resetStepOutputFeedback(currentStep.id));

      onSubmitted();
    } catch (error) {
      const errorDetails = extractErrorMessage(error);
      console.error("Failed to submit outputs:", error);
      onSubmitError?.();
      setApiErrorMessage(errorDetails);
    }
  }

  const [
    submitAdditionalInformation,
    { isLoading: isSubmittingAdditionalInfo },
  ] = useProceedWithAdditionalInformationMutation();

  async function handleAdditionalInfoSubmit() {
    if (!currentStep) return;

    try {
      setApiErrorMessage(undefined);
      await submitAdditionalInformation({
        workflowInstanceId,
        workflowStepInstanceId: currentStep.id,
      }).unwrap();

      onSubmitted();

      // Wipe the field state to prevent stale data
      dispatch(
        clearAllFieldState({
          feedbackType: "additionalInformation",
        }),
      );
    } catch (error) {
      const errorDetails = extractErrorMessage(error);
      console.error("Failed to submit additional information:", error);
      onSubmitError?.();
      setApiErrorMessage(errorDetails);
    }
  }

  const standardAndFileOutputIds =
    currentStep?.outputs
      ?.filter(
        (o) =>
          o.workflowStepOutputDefinition.type !== "Options" &&
          o.workflowStepOutputDefinition.type !== "File" &&
          o.workflowStepOutputDefinition.type !== "Files",
      )
      .map((o) => o.id) ?? [];

  const fileOutputIds =
    currentStep?.outputs
      ?.filter(
        (o) =>
          o.workflowStepOutputDefinition.type === "File" ||
          o.workflowStepOutputDefinition.type === "Files",
      )
      .map((o) => o.id) ?? [];

  const optionsOutputs =
    currentStep?.outputs?.filter(
      (o) => o.workflowStepOutputDefinition.type === "Options",
    ) ?? [];

  const standardAndFileValid = useSelector((state: RootState) =>
    standardAndFileOutputIds.length > 0 && currentStep
      ? selectCanSubmitStep(state, currentStep.id, standardAndFileOutputIds)
      : true,
  );

  const fileValid = useSelector((state: RootState) =>
    fileOutputIds.length > 0 && currentStep
      ? selectCanSubmitStep(state, currentStep.id, fileOutputIds)
      : true,
  );

  const optionsValid = useSelector((state: RootState) =>
    optionsOutputs.length > 0 && currentStep
      ? selectCanSubmitSelectStep(state, currentStep.id, optionsOutputs)
      : true,
  );

  const hasFeedback = useSelector((state: RootState) =>
    currentStep ? selectAnyOutputHasFeedback(state, currentStep.id) : false,
  );

  const additionalInformationRequests = useMemo(
    () =>
      currentStep?.additionalInformation.map((request, index) => ({
        fieldId: `req-${index}`,
        request,
      })) ?? [],
    [currentStep?.additionalInformation],
  );

  const canSubmitAdditionalInformation = useSelector((state: RootState) =>
    additionalInformationRequests.length > 0
      ? selectCanSubmitAdditionalInformationStep(
          state,
          additionalInformationRequests,
        )
      : true,
  );

  const canSubmit = standardAndFileValid && fileValid && optionsValid;

  const isReadOnly = currentStep
    ? isWorkflowCompleted || currentStep.status === "Completed"
    : false;

  // Sync submit action state upward for the standard outputs submit button
  useEffect(() => {
    if (!currentStep || isReadOnly || isAwaitingAgent) {
      onSubmitActionChange(null);
      return;
    }

    if (needsAdditionalInfo) {
      onSubmitActionChange({
        onSubmit: handleAdditionalInfoSubmit,
        isDisabled:
          !canSubmitAdditionalInformation || isSubmittingAdditionalInfo,
        isLoading: isSubmittingAdditionalInfo,
        label: t("additionalInformation.submitButton"),
      });

      return () => {
        onSubmitActionChange(null);
      };
    }

    let submitLabelKey:
      | "submitButton.submit"
      | "submitButton.submitWithFeedback"
      | "submitButton.complete" = "submitButton.submit";

    if (hasFeedback) {
      submitLabelKey = "submitButton.submitWithFeedback";
    } else if (isFinalStep) {
      submitLabelKey = "submitButton.complete";
    }

    const label = t(submitLabelKey);

    onSubmitActionChange({
      onSubmit: handleSubmit,
      isDisabled: !canSubmit,
      isLoading: isSubmitting,
      label,
    });

    return () => {
      onSubmitActionChange(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep?.id,
    isReadOnly,
    isAwaitingAgent,
    needsAdditionalInfo,
    canSubmitAdditionalInformation,
    isSubmittingAdditionalInfo,
    canSubmit,
    isSubmitting,
    hasFeedback,
    isFinalStep,
  ]);

  if (!currentStep) return null;

  if (needsAdditionalInfo) {
    return (
      <Suspense fallback={<Loader message="Loading step..." />}>
        <AdditionalInformationStep
          workflowInstanceId={workflowInstanceId}
          workflowStepInstanceId={currentStep.id}
          requestList={currentStep.additionalInformation ?? []}
          apiErrorMessage={apiErrorMessage}
        />
      </Suspense>
    );
  }

  if (isAwaitingAgent) return null;

  return (
    <Suspense fallback={<Loader message="Loading step..." />}>
      {currentStep.outputs?.map((output) => {
        const outputType = output.workflowStepOutputDefinition.type;

        if (outputType === "Standard") {
          return (
            <StandardOutputStep
              key={output.id}
              workflowInstanceId={workflowInstanceId}
              workflowStepInstanceId={currentStep.id}
              outputs={[output]}
              apiErrorMessage={apiErrorMessage}
              isReadOnly={isReadOnly}
            />
          );
        } else if (outputType === "File") {
          return (
            <FileOutputsStep
              key={output.id}
              workflowInstanceId={workflowInstanceId}
              workflowStepInstanceId={currentStep.id}
              outputs={[output]}
              apiErrorMessage={apiErrorMessage}
              isReadOnly={isReadOnly}
              multiple={false}
            />
          );
        } else if (outputType === "Files") {
          return (
            <FileOutputsStep
              key={output.id}
              workflowInstanceId={workflowInstanceId}
              workflowStepInstanceId={currentStep.id}
              outputs={[output]}
              apiErrorMessage={apiErrorMessage}
              isReadOnly={isReadOnly}
              multiple={true}
            />
          );
        } else if (outputType === "Options") {
          return (
            <SelectOutputStep
              key={output.id}
              workflowInstanceId={workflowInstanceId}
              workflowStepInstanceId={currentStep.id}
              outputs={[output]}
              selectionMode={getOptionsMode(currentStep)}
              apiErrorMessage={apiErrorMessage}
              isReadOnly={isReadOnly}
              isSubmitting={isSubmitting}
            />
          );
        }

        return null;
      })}

      {apiErrorMessage && !isReadOnly && (
        <Alert
          variant="error"
          title={apiErrorMessage.title}
          description={apiErrorMessage.message}
        />
      )}
    </Suspense>
  );
}
